/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useMemo } from "react";
import Container from "@/src/components/Container/Container";
import TeamSection from "@/src/components/Sections/Generic/TeamSection/TeamSection";
import LayoutPublicRoutes from "@/src/containers/Layouts/LayoutPublicRoutes";
import PartnerLogoSection from "@/src/components/Sections/Generic/PartnerLogoSection/PartnerLogoSection";
import BirdMapSection from "@/src/components/Sections/Divorce/BirdMapSection/BirdMapSection";
import DivorcePlannerSection from "@/src/components/Sections/Divorce/DivorcePlannerSection/DivorcePlannerSection";
import CustomerFeedback from "@/src/components/Sections/Generic/CustomerFeedback/CustomerFeedback";
import LandingPageTeaser from "@/src/components/Teaser/LandingPageTeaser";
import ServiceSection from "@/src/components/Sections/Generic/ServiceSection/ServiceSection";
import AdvantagesLegalbirdAttorneySection from "@/src/components/Sections/Generic/AdvantagesLegalbirdAttorneySection/AdvantagesLegalbirdAttorneySection";
import ProcedureDivorceSection from "@/src/components/Sections/Divorce/ProcedureDivorceSection/ProcedureDivorceSection";
import KindsOfDivorceSection from "@/src/components/Sections/Divorce/KindsOfDivorceSection/KindsOfDivorceSection";
import DivorcePartnerCheckin from "@/src/components/Checkin/FamilyLawCheckin/Divorce/DivorcePartnerCheckin";
import LpDivorceAdvoCardMeta from "./AdvoCard/LpDivorceAdvoCardMeta";
import LpDivorceAragMeta from "./Arag/LpDivorceAragMeta";
import LinkedCardBox from "@/src/components/LinkedCardBox/LinkedCardBox";
import { defaultProvenExpertTeaserCard } from "@/src/services/landingPageService";
import iconAttorneys from "@/src/assets/icons/generic/IconLawyers";
import costsSecureIcon from "@/src/assets/icons/generic/IconCostSecurity";
import { useCustomer } from "@/src/provider/Customer/CustomerProvider";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import { PARTNER_IRIS } from "@/src/constants/partnerIris";
import OurService from "@/src/components/Sections/Generic/OurService/OurService";

const getMetaDataByPartner = ({ partner }: { partner: string }) => {
  switch (partner) {
    case PARTNER_IRIS.ADVOCARD:
      return <LpDivorceAdvoCardMeta />;
    case PARTNER_IRIS.ARAG:
      return <LpDivorceAragMeta />;
    default:
      return null;
  }
};

const LpDivorcePartner = ({ partner }: { partner: NonNullable<AbstractCase["acquisitionPartner"]> }) => {
  const { isLoggedIn } = useCustomer();
  const metaData = useMemo(() => getMetaDataByPartner({ partner }), [partner]);

  return (
    <LayoutPublicRoutes minimal={!isLoggedIn} clickableLogo={isLoggedIn}>
      {metaData}
      <LandingPageTeaser
        headline={"Professionell scheiden lassen"}
        subheadline={"Wir begleiten Sie sicher bei Ihrer Scheidung."}
        teaserButtonLink={isLoggedIn ? "/servicewelt" : "#top-checkin"}
      >
        <LinkedCardBox
          linkedCards={[
            defaultProvenExpertTeaserCard,
            {
              icon: iconAttorneys,
              heading: "Professionelle Beratung",
            },
            {
              icon: costsSecureIcon,
              heading: "Nirgendwo günstiger",
            },
          ]}
        />
      </LandingPageTeaser>
      <PartnerLogoSection partnerIri={partner} containerStyle={"settlementContainer"} />
      <Container>{!isLoggedIn && <DivorcePartnerCheckin idPrefix={"top"} partner={partner} />}</Container>
      <KindsOfDivorceSection />
      <ServiceSection product={"divorce"} />
      <TeamSection isSubsection={true} />
      <BirdMapSection />
      <AdvantagesLegalbirdAttorneySection />
      <OurService />
      <DivorcePlannerSection />
      <ProcedureDivorceSection />
      <CustomerFeedback />
      <Container>{!isLoggedIn && <DivorcePartnerCheckin idPrefix={"top"} partner={partner} />}</Container>
    </LayoutPublicRoutes>
  );
};

export default LpDivorcePartner;
